/**
 * Helpers for the guide page node.
 *
 * The node stores a CMS query (tags). Rows come back from Storyblok the
 * same way tooltips do — they are not persisted on the node.
 */

import { helpStoryHref, safeHref } from '@/lib/safeHref';

export function normalizeTags(tags) {
  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  if (!Array.isArray(tags)) return [];
  return tags.map((tag) => String(tag || '').trim()).filter(Boolean);
}

export function queryKey(tags) {
  return normalizeTags(tags).slice().sort().join(',');
}

export function formatDuration(value) {
  if (value === 0 || value === '0') return '0min';
  if (value == null || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${value}min`;
  }
  const raw = String(value).trim();
  if (!raw) return '';
  if (/^\d+$/.test(raw)) return `${raw}min`;
  return raw;
}

export function normalizeGuide(guide, index = 0) {
  if (typeof guide === 'string') {
    const title = guide.trim();
    return title
      ? { id: `guide-${index}`, title, duration: '', href: '' }
      : null;
  }
  if (!guide || typeof guide !== 'object') return null;

  const content =
    guide.content && typeof guide.content === 'object' ? guide.content : {};
  const title = String(
    content.title ||
      content.name ||
      guide.title ||
      guide.name ||
      guide.slug ||
      ''
  ).trim();
  if (!title) return null;

  const slug = guide.full_slug || content.full_slug || guide.slug || '';
  const nestedLink =
    content.link && typeof content.link === 'object' ? content.link.url : '';
  const href = safeHref(
    content.url ||
      nestedLink ||
      content.href ||
      guide.href ||
      guide.url ||
      helpStoryHref(slug)
  );

  return {
    id: String(
      guide.uuid || guide.id || content.id || slug || `guide-${index}`
    ),
    title,
    duration: formatDuration(
      content.duration ||
        content.read_time ||
        content.minutes ||
        content.reading_time ||
        guide.duration
    ),
    href,
  };
}

export function normalizeGuides(guides) {
  if (!Array.isArray(guides)) return [];
  return guides.map(normalizeGuide).filter(Boolean);
}
